/**
 * Blocking via TanStack Query (docs/APP_PLAN.md §5.2, §12 M4; APP_SPEC
 * §15). This is the only place `apps/web` talks to `/api/blocks/*`.
 * Blocking here is purely a UI convenience over the server-authoritative
 * guard (docs/APP_PLAN.md §9.6) — the cache invalidation below just keeps
 * the friends/search screens from showing stale rows, it enforces nothing.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { BlockedUser } from '@perplexicon/api-client';

import { apiClient } from './api-client.js';
import {
  FRIENDS_INCOMING_QUERY_KEY,
  FRIENDS_OUTGOING_QUERY_KEY,
  FRIENDS_QUERY_KEY,
} from './friends.js';
import { GAME_SESSIONS_LIST_QUERY_KEY } from '../features/games/query-keys.js';

export const BLOCKED_USERS_QUERY_KEY = ['blocks', 'list'] as const;
const USER_SEARCH_QUERY_KEY_PREFIX = ['users', 'search'] as const;

export function useBlockedUsers(): UseQueryResult<BlockedUser[]> {
  return useQuery({ queryKey: BLOCKED_USERS_QUERY_KEY, queryFn: () => apiClient.blocks.list() });
}

export function useBlockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUsername: string) => apiClient.blocks.block(targetUsername),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BLOCKED_USERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FRIENDS_INCOMING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FRIENDS_OUTGOING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: USER_SEARCH_QUERY_KEY_PREFIX });
      // Blocking immediately forfeits every active session shared with the
      // blocked user, the blocker recorded as the loser (docs/APP_PLAN.md
      // §2.8, §8.6) — invalidate every session-list query so the
      // dashboard/History stop showing any such game as still in progress.
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_LIST_QUERY_KEY });
    },
  });
}

export function useUnblockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => apiClient.blocks.unblock(userId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: BLOCKED_USERS_QUERY_KEY }),
  });
}
